import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiDownload, FiArrowLeft, FiEye, FiFileText, FiArrowUp } from 'react-icons/fi';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './Report.css';
import Sidebar from '../../components/Sidebar/Sidebar';
import seedxpertLogo from '../../assets/images/dark-arkaplansiz.png';
import seedxpertDarkLogo from '../../assets/images/seedxpert-dark.png';
import reportFeatureImage from '../../assets/images/report-feature-image.webp';
import { useTranslation } from 'react-i18next';

import { Chart as ChartJS, LineElement, BarElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, ChartDataLabels } from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { FaArrowAltCircleUp, FaSortAmountUp, FaSortUp } from 'react-icons/fa';
import { getCompanyStageLabel, isDcfOnlyCompanyStage } from '../sectorOptions';

const Report = () => {
    const { t, i18n } = useTranslation();
    const { reportId } = useParams();
    const navigate = useNavigate();
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [berkusExchangeRate, setBerkusExchangeRate] = useState(1);
    const pdfPreviewRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        fetchReportData();
    }, [reportId]);

    useEffect(() => {
        const targetCurrency = reportData?.entrepreneurInfo?.valuationCurrency || '$';
        if (!reportData || targetCurrency === '$') {
            setBerkusExchangeRate(1);
            return;
        }

        let isCurrent = true;
        const fetchBerkusExchangeRate = async () => {
            try {
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
                if (!response.ok) throw new Error('Döviz kuru alınamadı');
                const data = await response.json();
                const targetRate = targetCurrency === '€' ? 1 : targetCurrency === '₺' ? data.rates?.TRY : data.rates?.USD;
                const usdRate = data.rates?.USD;
                if (isCurrent && Number.isFinite(targetRate) && Number.isFinite(usdRate) && usdRate > 0) {
                    setBerkusExchangeRate(targetRate / usdRate);
                }
            } catch (err) {
                console.error('Berkus değerlemesi için döviz kuru alınamadı:', err);
            }
        };

        fetchBerkusExchangeRate();
        return () => { isCurrent = false; };
    }, [reportData]);

    const formatAnswer = (answer) => {
        if (Array.isArray(answer)) {
            return answer.join(', ');
        }
        if (typeof answer === 'string') {
            return answer;
        }
        if (answer === null || answer === undefined) {
            return '';
        }
        return String(answer);
    };


const activeYears = useMemo(() => {
        if (!reportData) return ['2025', '2026', '2027', '2028', '2029'];

        // 1. Önce inputs içinden bak (Mevcut yöntem)
        let choice = reportData.inputs?.hasRevenue2025;

        // 2. Eğer orada yoksa, FinancialResults içine gömdüğümüz yere bak (Yedek yöntem)
        if (!choice && reportData.financialResults?.hasRevenue2025) {
            choice = reportData.financialResults.hasRevenue2025;
        }

        // 3. Karşılaştırmayı yap (String veya Boolean olabilir diye güvenli kontrol)
        const isNo = choice === 'Hayır' || choice === 'hayır' || choice === false;

        if (isNo) {
            return ['2026', '2027', '2028', '2029', '2030'];
        } else {
            return ['2025', '2026', '2027', '2028', '2029'];
        }
    }, [reportData]);

    const checkAnswerHasContent = (answer) => {
        if (Array.isArray(answer)) {
            return answer.length > 0 && answer.some(item => item && String(item).trim() !== '');
        }
        if (typeof answer === 'string') {
            return answer.trim() !== '';
        }
        return answer !== null && answer !== undefined && String(answer).trim() !== '';
    };

    const fetchReportData = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch(`/api/reports/${reportId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setReportData(data);
            } else {
                setError(t('report.notFound'));
            }
        } catch (err) {
            setError(t('report.loadingError'));
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('tr-TR').format(Math.round(num));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    const formatCurrency = (amount, currency = '$') => {
        return `${currency}${formatNumber(amount)}`;
    };

    const generatePDF = async () => {
        console.log('generatePDF çağrıldı');
        console.log('pdfPreviewRef.current:', pdfPreviewRef.current);

        if (!pdfPreviewRef.current) {
            console.error('pdfPreviewRef.current bulunamadı!');
            alert('PDF önizlemesi bulunamadı. Lütfen önce "CSS Önizleme" moduna geçin.');
            return;
        }

        setIsGeneratingPDF(true);

        try {
            const pages = pdfPreviewRef.current.querySelectorAll('.pdf-page');
            console.log('Bulunan sayfa sayısı:', pages.length);

            if (pages.length === 0) {
                throw new Error('Hiç sayfa bulunamadı!');
            }

            const pdf = new jsPDF({
                orientation: 'p',
                unit: 'mm',
                format: 'a4',
                compress: true
            });
            const pdfWidth = 210; 
            const pdfHeight = 297;

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                console.log(`Sayfa ${i + 1}/${pages.length} işleniyor...`);

                // Görsellerin yüklenmesini bekle
                const images = page.querySelectorAll('img');
                await Promise.all(
                    Array.from(images).map(img => {
                        if (img.complete) return Promise.resolve();
                        return new Promise((resolve) => {
                            img.onload = resolve;
                            img.onerror = resolve;
                            setTimeout(resolve, 100);
                        });
                    })
                );

                // Sayfayı canvas'a çevir
                const canvas = await html2canvas(page, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                    windowWidth: 794,
                    allowTaint: true,
                    removeContainer: true,
                    imageTimeout: 0,
                    foreignObjectRendering: false
                });

                console.log(`Canvas ${i + 1} oluşturuldu:`, canvas.width, 'x', canvas.height);

                // Canvas'ın doğru oluşturulduğundan emin ol
                if (canvas.width === 0 || canvas.height === 0) {
                    throw new Error(`Sayfa ${i + 1} için canvas boyutu geçersiz`);
                }

                const imgData = canvas.toDataURL('image/png');

                // Base64 verisinin doğru olduğundan emin ol
                if (!imgData || !imgData.startsWith('data:image/png;base64,')) {
                    throw new Error(`Sayfa ${i + 1} için görsel verisi oluşturulamadı`);
                }
                const imgWidth = pdfWidth;
                const imgHeight = (canvas.height * pdfWidth) / canvas.width;

                // Sayfa ekle (ilk sayfa hariç)
                if (i > 0) {
                    pdf.addPage();
                }

                // Görseli PDF'e ekle
                pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            }

            // PDF'i indir
            const isEnglish = i18n.language?.startsWith('en');
            const companyName = reportData.entrepreneurInfo?.companyName || (isEnglish ? 'Report' : 'Rapor');
            const fileName = isEnglish
                ? `${companyName}_Valuation_Report_${new Date().toLocaleDateString('en-US').replace(/\//g, '_')}.pdf`
                : `${companyName}_Değerleme_Raporu_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.pdf`;
            console.log('PDF indiriliyor:', fileName);
            pdf.save(fileName);

        } catch (error) {
            console.error('PDF oluşturma hatası:', error);
            alert('PDF oluşturulurken bir hata oluştu: ' + error.message);
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    // Cevap detaylarını grupla ve kategorilere göre ayır
    const groupAnswersByCategory = (answers, t) => {
        if (!answers) return {};

        const categoryMap = {
            'q1': t('report.categories.teamValue'), 'q2': t('report.categories.teamValue'), 'q3': t('report.categories.teamValue'),
            'q4': t('report.categories.teamValue'), 'q5': t('report.categories.teamValue'), 'q6': t('report.categories.teamValue'),
            'q7': t('report.categories.teamValue'), 'q8': t('report.categories.teamValue'), 'q9': t('report.categories.teamValue'),

            'q10': t('report.categories.ideaValue'), 'q11': t('report.categories.ideaValue'), 'q12': t('report.categories.ideaValue'),
            'q13': t('report.categories.ideaValue'), 'q14': t('report.categories.ideaValue'),

            'q20': t('report.categories.marketEvaluation'), 'q21': t('report.categories.marketEvaluation'), 'q22': t('report.categories.marketEvaluation'),
            'q23': t('report.categories.marketEvaluation'), 'q24': t('report.categories.marketEvaluation'), 'q25': t('report.categories.marketEvaluation'),
            'q26': t('report.categories.marketEvaluation'),

            'q30': t('report.categories.prototypeProduct'), 'q31': t('report.categories.prototypeProduct'), 'q32': t('report.categories.prototypeProduct'),
            'q33': t('report.categories.prototypeProduct'), 'q34': t('report.categories.prototypeProduct'), 'q35': t('report.categories.prototypeProduct'),
            'q36': t('report.categories.prototypeProduct'), 'q37': t('report.categories.prototypeProduct'), 'q38': t('report.categories.prototypeProduct'),

            'q40': t('report.categories.intellectualProperty'), 'q41': t('report.categories.intellectualProperty'), 'q42': t('report.categories.intellectualProperty'),
            'q43': t('report.categories.intellectualProperty'), 'q44': t('report.categories.intellectualProperty'), 'q45': t('report.categories.intellectualProperty'),
            'q46': t('report.categories.intellectualProperty'), 'q47': t('report.categories.intellectualProperty')
        };

        const grouped = {};

        answers.forEach(answer => {
            const category = categoryMap[answer.questionId] || t('report.categories.other');
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(answer);
        });

        return grouped;
    };

    if (loading) {
        
        return (
            <div className="report-container">
                <Sidebar />
                <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>{t('report.loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="report-container">
                <Sidebar />
                <div className="error">
                    <h2>{t('report.error')} {error}</h2>
                    <button onClick={() => navigate('/dashboard')}>{t('report.returnToDashboard')}</button>
                </div>
            </div>
        );
    }

    if (!reportData) {
        return (
            <div className="report-container">
                <Sidebar />
                <div className="error">{t('report.notFound')}</div>
            </div>
        );
    }

    const currency = reportData.entrepreneurInfo?.currency || '$';
    const valuationCurrency = reportData.entrepreneurInfo?.valuationCurrency || '$';
    const isDcfOnly = isDcfOnlyCompanyStage(reportData.entrepreneurInfo?.stage);
    const companyStageLabel = i18n.language?.startsWith('en') ? 'Company Stage' : 'Şirket Aşaması';
    const methodsUsedText = isDcfOnly
        ? (i18n.language?.startsWith('en') ? 'Discounted Cash Flow (DCF)' : 'İndirgenmiş Nakit Akışı (DCF)')
        : t('report.section1.methodsUsed');
    const groupedAnswers = groupAnswersByCategory(reportData.answers, t);

    const companyName = reportData.entrepreneurInfo?.companyName || 'Şirket';

    // Kategorileri al
    const categories = reportData.categoryResults || null;
    const getStrengthStyle = (strength) => {
        const ls = strength?.toLowerCase();
        if (ls === 'güçlü' || ls === 'strong') return 'badge-strong';
        if (ls === 'orta' || ls === 'medium') return 'badge-medium';
        if (ls === 'zayıf' || ls === 'weak') return 'badge-weak';
        return '';
    };

    const getStrengthText = (strength) => {
        const ls = strength?.toLowerCase();
        if (ls === 'güçlü' || ls === 'strong') return t('report.strength.strong');
        if (ls === 'orta' || ls === 'medium') return t('report.strength.medium');
        if (ls === 'zayıf' || ls === 'weak') return t('report.strength.weak');
        return strength;
    };

    const getInvestmentStageText = (stage) => {
        return getCompanyStageLabel(stage, i18n.language);
    };

    const getCategoryTitleTranslated = (categoryTitle) => {
        const categoryTitleMap = {
            // Turkish titles from backend (with numbers)
            '1. Takım Değeri': t('report.categories.teamValue'),
            '2. Fikrin Değeri': t('report.categories.ideaValue'),
            '3. Pazar Değerlendirmesi': t('report.categories.marketEvaluation'),
            '4. Prototip / Ürün Durumu': t('report.categories.prototypeProduct'),
            '5. Fikri Mülkiyet ve Psikoloji': t('report.categories.intellectualProperty'),
            '1. Team Value': t('report.categories.teamValue'),
            '2. Idea Value': t('report.categories.ideaValue'),
            '2. Value of the Idea': t('report.categories.ideaValue'),
            '3. Market Evaluation': t('report.categories.marketEvaluation'),
            '3. Market Assessment': t('report.categories.marketEvaluation'),
            '4. Prototype / Product Status': t('report.categories.prototypeProduct'),
            '5. Intellectual Property and Psychology': t('report.categories.intellectualProperty')
        };

        return categoryTitleMap[categoryTitle] || categoryTitle;
    };
    
    const getCategorySummary = (categoryTitle, strength) => {
    const categoryKeyMap = {
        '1. Takım Değeri': 'teamValue',
        '2. Fikrin Değeri': 'ideaValue',
        '2. Fikirin Değeri': 'ideaValue',
        '3. Pazar Değerlendirmesi': 'marketEvaluation',
        '4. Prototip / Ürün Durumu': 'prototypeProduct',
        '5. Fikri Mülkiyet ve Psikoloji': 'intellectualProperty',
        '1. Team Value': 'teamValue',
        '2. Value of the Idea': 'ideaValue',
        '3. Market Assessment': 'marketEvaluation',
        '4. Prototype / Product Status': 'prototypeProduct',
        '5. Intellectual Property and Psychology': 'intellectualProperty'
    };

    const strengthKeyMap = {
        'güçlü': 'strong', 'strong': 'strong',
        'orta': 'medium', 'medium': 'medium',
        'zayıf': 'weak', 'weak': 'weak'
    };

    const categoryKey = categoryKeyMap[categoryTitle];
    const strengthKey = strengthKeyMap[strength?.toLowerCase()];

    if (categoryKey && strengthKey) {
        // 1. Çeviri için tam anahtarı oluşturalım
        const translationKey = `report-2.categorySummaries.${categoryKey}.${strengthKey}`;

        // Sadece sorunlu kategori için konsola ne denediğimizi yazdıralım
        if (categoryTitle.includes('Fikrin')) {
             console.log(`[DEBUG] Çeviri deneniyor: Anahtar = "${translationKey}"`);
        }

        // 2. Çeviriyi alalım
        const translatedText = t(translationKey);

        // Sadece sorunlu kategori için çevirinin sonucunu yazdıralım
        if (categoryTitle.includes('Fikrin')) {
             console.log(`[DEBUG] t() fonksiyonundan gelen sonuç: "${translatedText}"`);
        }

        // 3. ÇEVİRİ BAŞARISIZ OLURSA KONTROL EDELİM
        // Eğer t() fonksiyonu metni bulamazsa, genellikle ya boş döner ya da anahtarın kendisini.
        if (!translatedText || translatedText === translationKey) {
            console.error(`[HATA] Çeviri BULUNAMADI: Anahtar = ${translationKey}`);
            
            // EKRANDA GÖRÜNMESİ İÇİN BİR HATA MESAJI DÖNDÜRELİM
            return `[ÇEVİRİ HATASI OLUŞTU: Anahtar '${translationKey}' dosyada bulunamadı.]`;
        }

        // 4. Başarılıysa, çevrilmiş metni döndürelim
        return translatedText;
    }

    return ''; // Eşleşme yoksa boş döndür
};
    const DotPattern = () => {
        return (
            <div class="dot-pattern">
                <div></div><div></div><div></div><div></div><div></div><div></div><div></div>
                <div></div><div></div><div></div><div></div><div></div><div></div><div></div>
                <div></div><div></div><div></div><div></div><div></div><div></div><div></div>
            </div>
        )
    }

    ChartJS.register(LineElement, BarElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

    const getNextFiveYears = () => {
        const year = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => (year + i).toString());
    };

    const LineAreaChartNext5Years = () => {
        // activeYears kullan
        const labels = activeYears; 
        
        const gelir = activeYears.map(y => reportData.financialResults?.totalIncome?.[y] || 0);
        const gider = activeYears.map(y => {
            return ((reportData.financialResults?.costOfSales?.[y] || 0) +
                (reportData.financialResults?.totalMarketingSalesExpenses?.[y] || 0) +
                (reportData.financialResults?.totalMarketingSalesExpenses?.[y] || 0) +
                (reportData.financialResults?.totalGeneralAdminExpenses?.[y] || 0) +
                (reportData.financialResults?.totalGeneralAdminExpenses?.[y] || 0) +
                (reportData.financialResults?.depreciationExpenses?.[y] || 0) +
                (reportData.financialResults?.taxAmount?.[y] || 0))
        })
        console.log(gider);

        const data = {
            labels,
            datasets: [
                {
                    label: t('report.chart.income'),
                    data: gelir,
                    borderColor: "#00FF40",
                    backgroundColor: "#00FF40",
                    fill: false, // 🔹 sadece çizgi
                    tension: 0.3,
                    borderWidth: 3,
                    pointRadius: 4,
                    yAxisID: "y"
                },
                {
                    label: t('report.chart.expense'),
                    data: gider,
                    borderColor: "#007BFF",
                    backgroundColor: "rgba(0,123,255,0.25)", // 🔹 saydam dolgu
                    fill: true, // 🔹 alan doldur
                    tension: 0.3,
                    borderWidth: 2,
                    pointRadius: 3,
                    yAxisID: "y"
                },
            ],
        };

        const options = {
            responsive: true,
            animation: false,
            plugins: {
                legend: {
                    display: true,
                    labels: { color: "#000000ff" },
                },
                tooltip: { enabled: true },
            },
            scales: {
                x: {
                    ticks: { color: "#aaa" },
                    grid: { color: "rgba(255,255,255,0.1)" },
                },
                y: {
                    beginAtZero: true,
                    ticks: { color: "#aaa" },
                    grid: { color: "rgba(255,255,255,0.1)" },
                },
            },
        };

        return (
            <div style={{ width: 480, height: 240, placeItems: 'center', placeSelf: 'center' }}>
                <Line data={data} options={options} />
            </div>
        );
    };

    // 🔹 Bar Chart
    const BarChartNext5Years = () => {
        const years = activeYears; 
    
    // 2. Labels için ilk yılı çıkar (Büyüme oranı hesabı için)
    const labels = activeYears.slice(1); 

   
        const salesRevenue = years.map(y =>
            reportData.financialResults?.netSalesRevenue?.[y] || 0
        );

        const chartData = [];
        const multipliers = [];

        for (let i = 1; i < salesRevenue.length; i++) {
            const growthRate = ((salesRevenue[i] - salesRevenue[i - 1]) / salesRevenue[i - 1]) * 100;
            const multiplier = salesRevenue[i] / salesRevenue[i - 1];
            chartData.push(growthRate);
            multipliers.push(multiplier);
        }

        // X ekseni etiketlerini özelleştir - yıl ve katsayı birlikte
        const customLabels = labels.map((year, index) => {
            return [`${year}`, `${multipliers[index].toFixed(2)}x`];
        });

        const data = {
            labels: customLabels,
            datasets: [{
                label: t('report.chart.growthRate'),
                data: chartData,
                backgroundColor: "#00FF40",
                borderRadius: 8,
            }],
        };

        const options = {
            animation: false,
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: true,
                    backgroundColor: 'rgba(34, 32, 57, 0.95)',
                    padding: 14,
                    titleFont: { size: 13, weight: 'bold' },
                    bodyFont: { size: 12 },
                    bodySpacing: 6,
                    callbacks: {
                        title: (context) => {
                            const year = labels[context[0].dataIndex];
                            const prev = parseInt(year) - 1;
                            return `${prev} → ${year}`;
                        },
                        label: (context) => {
                            return `${t('report.chart.growthRate')}: ${context.parsed.y.toFixed(1)}%`;
                        }
                    }
                },
                datalabels: {
                    display: false
                }
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: {
                        color: '#666',
                        font: { size: 11 },
                        maxRotation: 0,
                        minRotation: 0
                    }
                },
                y: {
                    beginAtZero: true,
                    grid: { color: "rgba(0,0,0,0.08)" },
                    ticks: {
                        color: '#666',
                        font: { size: 11 },
                        callback: (value) => value + '%'
                    }
                },
            }
        };

        return (
            <div style={{ width: 480, height: 240, placeItems: 'center', placeSelf: 'center' }}>
                <Bar data={data} options={options} />
            </div>
        );
    };

    const Progress = (valueText) => {
        let value = 67;
        if (valueText.valueText == 'Güçlü') value = 100;
        if (valueText.valueText == 'Zayıf') value = 25;

        const radius = 80;
        const stroke = 16;
        const normalizedRadius = radius - stroke * 2;
        const circumference = normalizedRadius * 2 * Math.PI;
        const strokeDashoffset = circumference - (value / 100) * circumference;

        return (
            <div>
                <svg height={200} width={200}>
                    {/* Arka plan halkası */}
                    <circle
                        stroke="#222039"
                        fill="transparent"
                        strokeWidth={stroke}
                        r={normalizedRadius}
                        cx={100}
                        cy={100}
                    />
                    {/* Dolan yeşil halka */}
                    <circle
                        stroke="#00FF40"
                        fill="transparent"
                        strokeWidth={stroke}
                        strokeDasharray={`${circumference} ${circumference}`}
                        style={{
                            strokeDashoffset,
                            transition: "stroke-dashoffset 0.6s ease",
                            transform: "rotate(-90deg)",
                            transformOrigin: "50% 50%",
                        }}
                        r={normalizedRadius}
                        cx={100}
                        cy={100}
                    />
                    {/* Orta yüzde metni */}
                    <text
                        x="50%"
                        y="52%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#00FF40"
                        fontSize="28px"
                        fontWeight="bold"
                    >
                        {value}%
                    </text>
                </svg>
            </div>
        );
    }
    console.log("RAPOR VERİSİNDEKİ KATEGORİLER:", reportData.categoryResults);
    return (
        <div className="report-content">
            <Sidebar />
            <div className="">
                <div style={{ position: "absolute", bottom: '100vh', minWidth: '900px' }}>
                    <div className="pdf-preview-container" ref={pdfPreviewRef}>
                        {/* KAPAK SAYFASI */}
                        <div className="pdf-page pdf-cover-page">
                            <div className='pdf-cover-dot-area'>
                                <DotPattern />
                            </div>
                            <div className='pdf-cover-seedxpert-logo-area'>
                                <img alt="SeedXpert Logo" className="pdf-cover-seedxpert-logo" src={seedxpertDarkLogo}></img>
                            </div>
                            <div className="pdf-cover-green-line"></div>
                            <div className="pdf-cover-content pdf-cover-bg">
                                <img alt="Rapor Görseli" className='pdf-cover-image' src={reportFeatureImage}></img>
                                <div className='pdf-cover-title'>
                                    <div className="pdf-cover-info-label">{t('report.pdf.coverTitle')}<br></br>
                                        {t('report.pdf.coverTitleLine2')}</div>
                                    <span className="pdf-cover-info-value">{formatDate(reportData.createdAt)}</span>
                                </div>
                            </div>
                            <div className="pdf-cover-footer">
                                <div className="pdf-cover-footer-text">{t('report.pdf.coverConfidential')}</div>
                                <div className="pdf-cover-powered-by">{t('report.pdf.poweredBy')}</div>
                            </div>
                        </div>

                        {/* PAGE 1 - Giriş */}
                        <div className="pdf-page">
                            <div className="pdf-cover-accent"></div>
                            <div className='pdf-page-dot-area'>
                                <DotPattern />
                            </div>
                            <div className="pdf-header">
                                <div className="pdf-header-title"></div>
                                <div className='pdf-cover-logo-area'>
                                    {reportData.entrepreneurInfo?.companyLogo && (
                                        <img
                                            src={reportData.entrepreneurInfo.companyLogo}
                                            alt="Logo"
                                            className="pdf-cover-logo"
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="pdf-content">
                                <div className="pdf-page-title">{t('report.section1.title')}</div>
                                <br />
                                <div className="pdf-info-row">
                                    <span className="pdf-info-label">{t('report.section1.companyName')} </span>
                                    <span className="pdf-info-value">{companyName}</span>
                                </div>
                                <div className="pdf-info-row">
                                    <span className="pdf-info-label">{t('report.section1.foundingYear')} </span>
                                    <span className="pdf-info-value">{formatDate(reportData.entrepreneurInfo?.foundingDate)}</span>
                                </div>
                                <div className="pdf-info-row">
                                <span className="pdf-info-label">{t('report.section1.activitySector')} </span>
                                <span className="pdf-info-value">
                                    {reportData.entrepreneurInfo?.sector
                                        ? t(`sharedEntrepreneurInfo.sectors.${reportData.entrepreneurInfo.sector}`, { defaultValue: reportData.entrepreneurInfo.sector })
                                        : '-'}
                                </span>
                            </div>
                            <div className="pdf-info-row">
                                <span className="pdf-info-label">{companyStageLabel} : </span>
                                <span className="pdf-info-value">{getInvestmentStageText(reportData.entrepreneurInfo?.stage)}</span>
                            </div>
                                <div className="pdf-info-row">
                                    <span className="pdf-info-label">{t('report.section1.valuationMethods')} </span>
                                    <span className="pdf-info-value">{methodsUsedText}</span>
                                </div>
                                <br></br>
                                <div className="pdf-page-title">{t('report.section2.title')}</div>
                                <br/>
                                <div className='pdf-page-text'>
                                    {!isDcfOnly && <p>
                                        <span className="pdf-info-label">{t('report.section2.methodsTitle')}</span> <br />
                                        <b>{t('report.section2.berkus.title')}</b> <br />
                                        {t('report.section2.berkus.desc')}
                                    </p>}
                                    {!isDcfOnly && <ul style={{ marginLeft: '1.5rem' }}>
                                        <li>{t('report.section2.berkus.bullet1')}</li>
                                        <li>{t('report.section2.berkus.bullet2')}</li>
                                        <li>{t('report.section2.berkus.bullet3')}</li>
                                    </ul>}
                                    <p>
                                        <b>{isDcfOnly
                                            ? (i18n.language?.startsWith('en') ? '1-Discounted Cash Flow (DCF)' : '1-İndirgenmiş Nakit Akışı (DCF)')
                                            : t('report.section2.dcf.title')}</b><br /> {t('report.section2.dcf.desc')}
                                    </p>
                                    <br />
                                    <p>
                                        <b>{t('report.section2.assumptions.title')}</b><br />
                                        {t('report.section2.assumptions.text')}
                                    </p>
                                    <br />
                                    <p>
                                        <b>{t('report.section2.dataSources.title')}</b><br />
                                        {t('report.section2.dataSources.text')}<br />
                                        {t('report.section2.dataSources.text2')}
                                    </p>
                                </div>
                            </div>
                            <div className="pdf-footer">
                                <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                <span className="pdf-page-number">{t('report.pdf.page')} 1</span>
                            </div>
                        </div>

                        {!isDcfOnly && <>
                        {/* PAGE 2 - BERKUS */}
                        <div className="pdf-page">
                            <div className="pdf-cover-accent"></div>
                            <div className='pdf-page-dot-area'>
                                <DotPattern />
                            </div>
                            <div className="pdf-header">
                                <div className="pdf-header-title"></div>
                                <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                            </div>
                            <div className="pdf-content">
                                <div className="pdf-page-title">{t('report.section3.title')}</div>
                                {categories[0] && (
                                    <div className="pdf-category-section">
                                        <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[0].categoryTitle)}</h3>
                                        <div className="pdf-category-header">
                                            <Progress valueText={categories[0].strength} />
                                            <p className="pdf-category-summary">{getCategorySummary(categories[0].categoryTitle, categories[0].strength)}</p>
                                        </div>

                                    </div>
                                )}
                               {categories[1] && (
                                <div className="pdf-category-section">
                                    <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[1].categoryTitle)}</h3>
                                    <div className="pdf-category-header">
                                        <Progress valueText={categories[1].strength} />
                                        {/* DOĞRU KULLANIM: Dinamik çeviri fonksiyonunu çağırıyor */}
                                        <p className="pdf-category-summary">{getCategorySummary(categories[1].categoryTitle, categories[1].strength)}</p>
                                    </div>
                                </div>
                            )}
                                {categories[2] && (
                                    <div className="pdf-category-section">
                                        <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[2].categoryTitle)}</h3>
                                        <div className="pdf-category-header">
                                            <Progress valueText={categories[2].strength} />
                                            <p className="pdf-category-summary">{getCategorySummary(categories[2].categoryTitle, categories[2].strength)}</p>
                                        </div>

                                    </div>
                                )}
                            </div>
                            <div className="pdf-footer">
                                <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                <span className="pdf-page-number">{t('report.pdf.page')} 2</span>
                            </div>
                        </div>

                        {/* PAGE 3 - BERKUS */}
                        <div className="pdf-page">
                            <div className="pdf-cover-accent"></div>
                            <div className='pdf-page-dot-area'>
                                <DotPattern />
                            </div>
                            <div className="pdf-header">
                                <div className="pdf-header-title"></div>
                                <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                            </div>
                            <div className="pdf-content">
                                {categories[3] && (
                                    <div className="pdf-category-section">
                                        <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[3].categoryTitle)}</h3>
                                        <div className="pdf-category-header">
                                            <Progress valueText={categories[3].strength} />
                                            <p className="pdf-category-summary">{getCategorySummary(categories[3].categoryTitle, categories[3].strength)}</p>
                                        </div>

                                    </div>
                                )}
                                {categories[4] && (
                                    <div className="pdf-category-section">
                                        <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[4].categoryTitle)}</h3>
                                        <div className="pdf-category-header">
                                            <Progress valueText={categories[4].strength} />
                                            <p className="pdf-category-summary">{getCategorySummary(categories[4].categoryTitle, categories[4].strength)}</p>
                                        </div>

                                    </div>
                                )}


                                <div className="pdf-category-result-section">
                                    <div>{t('report.section3.analysisResult')}</div>
                                    <div><span className="pdf-score-unit">%{Math.round(reportData.overallPercentage || 0)}</span></div>
                                </div>
                            </div>
                            <div className="pdf-footer">
                                <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                <span className="pdf-page-number">{t('report.pdf.page')} 3</span>
                            </div>
                        </div>

                        </>}

                        {/* PAGE 4 - Finansal Tablo */}
                        <div className="pdf-page">
                            <div className="pdf-cover-accent"></div>
                            <div className='pdf-page-dot-area'>
                                <DotPattern />
                            </div>
                            <div className="pdf-header">
                                <div className="pdf-header-title"></div>
                                <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                            </div>
                            <div className="pdf-content">
                                <div className="pdf-page-title">{t('report.section4.title')}</div>
                                <div className='pdf-page-text'>
                                    <p>
                                        {companyName} {t('report.section4.intro')}
                                    </p>
                                </div>
                                <div className="pdf-table-section">
                                    <h3 className="pdf-table-title">{t('report.section4.tableTitle')}</h3>
                                    <table className="pdf-table"  style={{minWidth:0}}>
                                        <thead className="pdf-table-header">

                                            <tr>
                                                <th className="pdf-table-cell-header pdf-table-cell-first">
                                                    {t('report.table.financialItem')}
                                                </th>
                                                {activeYears.map(year => (
                                                <th key={year} className="pdf-table-cell-header">{year}</th>
                                            ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.salesRevenue')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.netSalesRevenue?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.totalIncome')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.totalIncome?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.costOfSales')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.costOfSales?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.marketingSales')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.totalMarketingSalesExpenses?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.rdExpenses')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.totalMarketingSalesExpenses?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.generalAdmin')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.totalGeneralAdminExpenses?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.financing')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.totalGeneralAdminExpenses?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row pdf-table-row-highlight">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.grossProfit')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.grossProfit?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.depreciation')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.depreciationExpenses?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.taxAmount')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.taxAmount?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebit')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.ebit?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebitPercent')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {reportData.financialResults?.percentageEbit?.[y].toFixed(2) || 0}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebitda')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {formatCurrency(reportData.financialResults?.ebitda?.[y] || 0, currency)}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr className="pdf-table-row">
                                                <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebitdaPercent')}</td>
                                                {activeYears.map(y => (
                                                    <td key={y} className="pdf-table-cell pdf-table-cell-year">
                                                        {reportData.financialResults?.percentageEbitda?.[y].toFixed(2) || 0}
                                                    </td>
                                                ))}
                                            </tr>

                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="pdf-footer">
                                <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                <span className="pdf-page-number">{t('report.pdf.page')} {isDcfOnly ? 2 : 4}</span>
                            </div>
                        </div>

                        {/* PAGE 5 - Grafikler */}
                        <div className="pdf-page">
                            <div className="pdf-cover-accent"></div>
                            <div className='pdf-page-dot-area'>
                                <DotPattern />
                            </div>
                            <div className="pdf-header">
                                <div className="pdf-header-title"></div>
                                <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                            </div>
                            <div className="pdf-content">
                                <div className="pdf-page-sub-title">{t('report.section5.chartTitle1')}</div>
                                <LineAreaChartNext5Years />
                                <br />
                                <div className="pdf-page-sub-title">{t('report.section5.chartTitle2')}</div>
                                <BarChartNext5Years />
                                <br />
                                <div className="pdf-page-title">{t('report.section5.title')}</div>
                                <div className="pdf-valuation-section">
                                    <div className="pdf-valuation-grid" style={{ gridTemplateColumns: isDcfOnly ? '1fr' : undefined }}>
                                        {!isDcfOnly && <div className="pdf-valuation-card">
                                            <div className="pdf-valuation-method">{t('report.section5.berkusMethod')}</div>
                                            <div className="pdf-valuation-amount">
                                                {formatCurrency((reportData.totalScore || 0) * berkusExchangeRate, valuationCurrency)}
                                            </div>
                                            <FaArrowAltCircleUp size={45} color='#2bd52d' />
                                        </div>}
                                        <div className="pdf-valuation-card">
                                            <div className="pdf-valuation-method">{t('report.section5.dcfMethod')}</div>
                                            <div className="pdf-valuation-amount">
                                                {formatCurrency(reportData.financialResults?.valuationMetric || 0, valuationCurrency)}
                                            </div>
                                            <FaArrowAltCircleUp size={45} color='#2bd52d' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="pdf-footer">
                                <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                <span className="pdf-page-number">{t('report.pdf.page')} {isDcfOnly ? 3 : 5}</span>
                            </div>
                        </div>

                    </div>
                </div>
                <>
                    <div className="report-header">
                        <div className="company-header">
                            <div className="company-info-section">
                                {reportData.entrepreneurInfo?.companyLogo && (
                                    <div className="company-logo">
                                        <img
                                            src={reportData.entrepreneurInfo.companyLogo}
                                            alt={`${reportData.entrepreneurInfo?.companyName || 'Şirket'} logosu`}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="company-basic-info">
                                    <h1>{reportData.entrepreneurInfo?.companyName || t('report.company')} {t('report.valuationReport')}</h1>
                                </div>
                            </div>
                        </div>

                        <div className="company-info-grid">
                            <div className="info-row">
                                <div className="info-item">
                                    <label>{t('report.sector')}</label>
                                    <span>
                                    {reportData.entrepreneurInfo?.sector
                                        ? t(`sharedEntrepreneurInfo.sectors.${reportData.entrepreneurInfo.sector}`, { defaultValue: reportData.entrepreneurInfo.sector })
                                        : '-'}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>{companyStageLabel}</label>
                                    <span>{getInvestmentStageText(reportData.entrepreneurInfo?.stage)}</span>
                                </div>
                            </div>
                            <div className="info-row">
                                <div className="info-item">
                                    <label>{t('report.foundingDate')}</label>
                                    <span>{formatDate(reportData.entrepreneurInfo?.foundingDate)}</span>
                                </div>
                                <div className="info-item">
                                    <label>{t('report.reportDate')}</label>
                                    <span>{formatDate(reportData.createdAt)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='viewedReport'>
                        <div className="pdf-preview-container">
                            {/* KAPAK SAYFASI */}
                            <div className="pdf-page pdf-cover-page">
                                <div className='pdf-cover-dot-area'>
                                    <DotPattern />
                                </div>
                                <div className='pdf-cover-seedxpert-logo-area'>
                                    <img alt="SeedXpert Logo" className="pdf-cover-seedxpert-logo" src={seedxpertDarkLogo}></img>
                                </div>
                                <div className="pdf-cover-green-line"></div>
                                <div className="pdf-cover-content pdf-cover-bg">
                                    <img alt="Rapor Görseli" className='pdf-cover-image' src={reportFeatureImage}></img>
                                    <div className='pdf-cover-title'>
                                        <div className="pdf-cover-info-label">{t('report.pdf.coverTitle')}<br></br>
                                            {t('report.pdf.coverTitleLine2')}</div>
                                        <span className="pdf-cover-info-value">{formatDate(reportData.createdAt)}</span>
                                    </div>
                                </div>
                                <div className="pdf-cover-footer">
                                    <div className="pdf-cover-footer-text">{t('report.pdf.coverConfidential')}</div>
                                    <div className="pdf-cover-powered-by">{t('report.pdf.poweredBy')}</div>
                                </div>
                            </div>

                            {/* PAGE 1 - Giriş */}
                            <div className="pdf-page">
                                <div className="pdf-cover-accent"></div>
                                <div className='pdf-page-dot-area'>
                                    <DotPattern />
                                </div>
                                <div className="pdf-header">
                                    <div className="pdf-header-title"></div>
                                    <div className='pdf-cover-logo-area'>
                                        {reportData.entrepreneurInfo?.companyLogo && (
                                            <img
                                                src={reportData.entrepreneurInfo.companyLogo}
                                                alt="Logo"
                                                className="pdf-cover-logo"
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="pdf-content">
                                    <div className="pdf-page-title">{t('report.section1.title')}</div>
                                    <br />
                                    <div className="pdf-info-row">
                                        <span className="pdf-info-label">{t('report.section1.companyName')} </span>
                                        <span className="pdf-info-value">{companyName}</span>
                                    </div>
                                    <div className="pdf-info-row">
                                        <span className="pdf-info-label">{t('report.section1.foundingYear')} </span>
                                        <span className="pdf-info-value">{formatDate(reportData.entrepreneurInfo?.foundingDate)}</span>
                                    </div>
                                   <div className="pdf-info-row">
                                    <span className="pdf-info-label">{t('report.section1.activitySector')} </span>
                                    <span className="pdf-info-value">
                                        {/* BURASI DEĞİŞTİ: sector verisi ve çevirisi eklendi */}
                                        {reportData.entrepreneurInfo?.sector
                                            ? t(`sharedEntrepreneurInfo.sectors.${reportData.entrepreneurInfo.sector}`, { defaultValue: reportData.entrepreneurInfo.sector })
                                            : '-'}
                                    </span>
                                </div>
                                <div className="pdf-info-row">
                                    <span className="pdf-info-label">{companyStageLabel} : </span>
                                    {/* BURASI DEĞİŞTİ: stage verisi eklendi */}
                                    <span className="pdf-info-value">{getInvestmentStageText(reportData.entrepreneurInfo?.stage)}</span>
                                </div>
                                    <div className="pdf-info-row">
                                    <span className="pdf-info-label">{t('report.section1.valuationMethods')} </span>
                                    <span className="pdf-info-value">{methodsUsedText}</span>
                                    </div>
                                    <br></br>
                                    <div className="pdf-page-title">{t('report.section2.title')}</div>
                                    <br />
                                    <div className='pdf-page-text'>
                                        {!isDcfOnly && <p>
                                            <span className="pdf-info-label">{t('report.section2.methodsTitle')}</span> <br />
                                            <b>{t('report.section2.berkus.title')}</b> <br />
                                            {t('report.section2.berkus.desc')}
                                        </p>}
                                        {!isDcfOnly && <ul style={{ marginLeft: '1.5rem' }}>
                                            <li>{t('report.section2.berkus.bullet1')}</li>
                                            <li>{t('report.section2.berkus.bullet2')}</li>
                                            <li>{t('report.section2.berkus.bullet3')}</li>
                                        </ul>}
                                        <p>
                                            <b>{isDcfOnly
                                                ? (i18n.language?.startsWith('en') ? '1-Discounted Cash Flow (DCF)' : '1-İndirgenmiş Nakit Akışı (DCF)')
                                                : t('report.section2.dcf.title')}</b><br /> {t('report.section2.dcf.desc')}
                                        </p>
                                        <br />
                                        <p>
                                            <b>{t('report.section2.assumptions.title')}</b><br />
                                            {t('report.section2.assumptions.text')}
                                        </p>
                                        <br />
                                        <p>
                                            <b>{t('report.section2.dataSources.title')}</b><br />
                                            {t('report.section2.dataSources.text')}<br />
                                            {t('report.section2.dataSources.text2')}
                                        </p>
                                    </div>
                                </div>
                                <div className="pdf-footer">
                                    <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                    <span className="pdf-page-number">{t('report.pdf.page')} 1</span>
                                </div>
                            </div>

                            {!isDcfOnly && <>
                            {/* PAGE 2 - BERKUS */}
                            <div className="pdf-page">
                                <div className="pdf-cover-accent"></div>
                                <div className='pdf-page-dot-area'>
                                    <DotPattern />
                                </div>
                                <div className="pdf-header">
                                    <div className="pdf-header-title"></div>
                                    <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                                </div>
                                <div className="pdf-content">
                                    <div className="pdf-page-title">{t('report.section3.title')}</div>
                                    {categories[0] && (
                                        <div className="pdf-category-section">
                                            <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[0].categoryTitle)}</h3>
                                            <div className="pdf-category-header">
                                                <Progress valueText={categories[0].strength} />
                                                <p className="pdf-category-summary">{getCategorySummary(categories[0].categoryTitle, categories[0].strength)}</p>
                                            </div>

                                        </div>
                                    )}
                                   {categories[1] && (
                                    <div className="pdf-category-section">
                                        <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[1].categoryTitle)}</h3>
                                        <div className="pdf-category-header">
                                            <Progress valueText={categories[1].strength} />
                                            {/* DOĞRU KULLANIM: Dinamik çeviri fonksiyonunu çağırıyor */}
                                            <p className="pdf-category-summary">{getCategorySummary(categories[1].categoryTitle, categories[1].strength)}</p>
                                        </div>
                                    </div>
                                )}
                                    {categories[2] && (
                                        <div className="pdf-category-section">
                                            <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[2].categoryTitle)}</h3>
                                            <div className="pdf-category-header">
                                                <Progress valueText={categories[2].strength} />
                                                <p className="pdf-category-summary">{getCategorySummary(categories[2].categoryTitle, categories[2].strength)}</p>
                                            </div>

                                        </div>
                                    )}
                                </div>
                                <div className="pdf-footer">
                                    <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                    <span className="pdf-page-number">{t('report.pdf.page')} 2</span>
                                </div>
                            </div>

                            {/* PAGE 3 - BERKUS */}
                            <div className="pdf-page">
                                <div className="pdf-cover-accent"></div>
                                <div className='pdf-page-dot-area'>
                                    <DotPattern />
                                </div>
                                <div className="pdf-header">
                                    <div className="pdf-header-title"></div>
                                    <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                                </div>
                                <div className="pdf-content">
                                    {categories[3] && (
                                        <div className="pdf-category-section">
                                            <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[3].categoryTitle)}</h3>
                                            <div className="pdf-category-header">
                                                <Progress valueText={categories[3].strength} />
                                                <p className="pdf-category-summary">{getCategorySummary(categories[3].categoryTitle, categories[3].strength)}</p>
                                            </div>

                                        </div>
                                    )}
                                    {categories[4] && (
                                        <div className="pdf-category-section">
                                            <h3 className="pdf-category-title">{getCategoryTitleTranslated(categories[4].categoryTitle)}</h3>
                                            <div className="pdf-category-header">
                                                <Progress valueText={categories[4].strength} />
                                                <p className="pdf-category-summary">{getCategorySummary(categories[4].categoryTitle, categories[4].strength)}</p>
                                            </div>

                                        </div>
                                    )}


                                    <div className="pdf-category-result-section">
                                        <div>{t('report.section3.analysisResult')}</div>
                                        <div><span className="pdf-score-unit">%{Math.round(reportData.overallPercentage || 0)}</span></div>
                                    </div>
                                </div>
                                <div className="pdf-footer">
                                    <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                    <span className="pdf-page-number">{t('report.pdf.page')} 3</span>
                                </div>
                            </div>

                            </>}

                            {/* PAGE 4 - Finansal Tablo */}
                            <div className="pdf-page">
                                <div className="pdf-cover-accent"></div>
                                <div className='pdf-page-dot-area'>
                                    <DotPattern />
                                </div>
                                <div className="pdf-header">
                                    <div className="pdf-header-title"></div>
                                    <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                                </div>
                                <div className="pdf-content">
                                    <div className="pdf-page-title">{t('report.section4.title')}</div>
                                    <div className='pdf-page-text'>
                                        <p>
                                            {companyName} {t('report.section4.intro')}
                                        </p>
                                    </div>
                                    <div className="pdf-table-section">
                                        <h3 className="pdf-table-title">{t('report.section4.tableTitle')}</h3>
                                        <table className="pdf-table" style={{ minWidth: 0, fontSize: '9px' }}> {/* Font boyutu sığması için küçültüldü */}
    <thead className="pdf-table-header">
        <tr>
            {/* DÜZELTME 1: Eksik olan ilk başlık hücresi eklendi */}
            <th className="pdf-table-cell-header pdf-table-cell-first">
                {t('report.table.financialItem')}
            </th>
            {activeYears.map(year => (
                <th key={year} className="pdf-table-cell-header">{year}</th>
            ))}
        </tr>
    </thead>
    <tbody>
        {/* Mevcut satırlar... */}
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.salesRevenue')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.netSalesRevenue?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.totalIncome')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.totalIncome?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.costOfSales')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.costOfSales?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.marketingSales')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.totalMarketingSalesExpenses?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.rdExpenses')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.totalRDExpenses?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.generalAdmin')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.totalGeneralAdminExpenses?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.financing')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.totalFinancingExpenses?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row pdf-table-row-highlight">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.grossProfit')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.grossProfit?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.depreciation')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.depreciationExpenses?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.taxAmount')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.taxAmount?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebit')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.ebit?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebitPercent')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {reportData.financialResults?.percentageEbit?.[y] ? `%${reportData.financialResults.percentageEbit[y].toFixed(2)}` : '%0.00'}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebitda')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {formatCurrency(reportData.financialResults?.ebitda?.[y] || 0, currency)}
                </td>
            ))}
        </tr>
        <tr className="pdf-table-row">
            <td className="pdf-table-cell pdf-table-cell-first pdf-table-cell-bold">{t('report.table.ebitdaPercent')}</td>
            {activeYears.map(y => (
                <td key={y} className="pdf-table-cell pdf-table-cell-year">
                    {reportData.financialResults?.percentageEbitda?.[y] ? `%${reportData.financialResults.percentageEbitda[y].toFixed(2)}` : '%0.00'}
                </td>
            ))}
        </tr>

      
    </tbody>
</table>
                                    </div>
                                </div>
                                <div className="pdf-footer">
                                    <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                    <span className="pdf-page-number">{t('report.pdf.page')} {isDcfOnly ? 2 : 4}</span>
                                </div>
                            </div>

                            {/* PAGE 5 - Grafikler */}
                            <div className="pdf-page">
                                <div className="pdf-cover-accent"></div>
                                <div className='pdf-page-dot-area'>
                                    <DotPattern />
                                </div>
                                <div className="pdf-header">
                                    <div className="pdf-header-title"></div>
                                    <div className="pdf-header-date">{companyName} • {formatDate(reportData.createdAt)}</div>
                                </div>
                                <div className="pdf-content">
                                    <div className="pdf-page-sub-title">{t('report.section5.chartTitle1')}</div>
                                    <LineAreaChartNext5Years />
                                    <br />
                                    <div className="pdf-page-sub-title">{t('report.section5.chartTitle2')}</div>
                                    <BarChartNext5Years />
                                    <br />
                                    <div className="pdf-page-title">{t('report.section5.title')}</div>
                                    <div className="pdf-valuation-section">
                                        <div className="pdf-valuation-grid" style={{ gridTemplateColumns: isDcfOnly ? '1fr' : undefined }}>
                                            {!isDcfOnly && <div className="pdf-valuation-card">
                                                <div className="pdf-valuation-method">{t('report.section5.berkusMethod')}</div>
                                                <div className="pdf-valuation-amount">
                                                    {formatCurrency((reportData.totalScore || 0) * berkusExchangeRate, valuationCurrency)}
                                                </div>
                                                <FaArrowAltCircleUp size={45} color='#2bd52d' />
                                            </div>}
                                            <div className="pdf-valuation-card">
                                                <div className="pdf-valuation-method">{t('report.section5.dcfMethod')}</div>
                                                <div className="pdf-valuation-amount">
                                                    {formatCurrency(reportData.financialResults?.valuationMetric || 0, valuationCurrency)}
                                                </div>
                                                <FaArrowAltCircleUp size={45} color='#2bd52d' />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="pdf-footer">
                                    <span className="pdf-footer-text">{t('report.pdf.footer')}</span>
                                    <span className="pdf-page-number">{t('report.pdf.page')} {isDcfOnly ? 3 : 5}</span>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div className="valuation-summary">

                        <div>
                            <div className="print-actions">
                                <button
                                    className="print-btn"
                                    onClick={async () => {
                                        await new Promise(resolve => setTimeout(resolve, 1000));
                                        generatePDF();
                                    }}
                                    disabled={isGeneratingPDF}
                                >
                                    <FiDownload /> {isGeneratingPDF ? t('report.pdfPreparing') : t('report.pdfDownload')}
                                </button>
                                <button className="dashboard-btn" onClick={() => navigate('/dashboard')}>
                                    <FiArrowLeft /> {t('report.returnToReports')}
                                </button>
                            </div>
                        </div>
                    </div>
                </>

            </div>
        </div>
    );
};

export default Report;
